/* © NXAPP Limited */
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { describeError } from '@/lib/describeError'
import { useAuthContext } from '@/context/AuthContext'
import { ArrowLeft, Mail, FileText, Users, Send, Workflow, Pencil, Lock, Info, CalendarClock, Phone } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import IssuesContent from '@/pages/issues/IssuesContent'
import TemplatesContent from '@/pages/templates/TemplatesContent'
import MembersContent from './MembersContent'
import SequenceContent from './SequenceContent'
import IndividualContent from './IndividualContent'
import WeeklyContent from './WeeklyContent'
import TMPage from '@/pages/tm/TMPage'
import type { CampaignRow, ProfileOption } from './CampaignsPage'

type Tab = 'issues' | 'templates' | 'members' | 'sequence' | 'outreach' | 'weekly' | 'queue'

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const qc = useQueryClient()
  // null until the campaign loads — a sequence campaign opens on Sequence,
  // a broadcast campaign on Issues.
  const [tabOverride, setTabOverride] = useState<Tab | null>(null)
  // The sending profile carries the brand, and the brand decides whose
  // unsubscribes apply. Changing it by a stray click could email people who
  // opted out, so it stays locked until explicitly unlocked.
  const [profileUnlocked, setProfileUnlocked] = useState(false)
  const [editingTitle, setEditingTitle] = useState<{ name: string; description: string } | null>(null)
  // Default audience locks once set — a stray click changing who a weekly issue
  // targets is exactly the mistake this guards against.
  const [audienceUnlocked, setAudienceUnlocked] = useState(false)

  const { data: segments } = useQuery({
    queryKey: ['segments-for-campaign', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('segments').select('id, name').eq('user_id', user!.id).order('name')
      return (data ?? []) as { id: string; name: string }[]
    },
  })

  const setDefaultAudience = useMutation({
    mutationFn: async (audience: { campaignLeads: boolean; allSubscribed: boolean; segmentIds: string[] }) => {
      const { error } = await supabase.from('campaigns')
        .update({ default_audience: audience }).eq('id', campaignId!).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  })

  const { data: profileOptions } = useQuery({
    queryKey: ['sending-profiles-options', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('sending_profiles')
        .select('id, name, from_email, brand_id, brands(name)')
        .eq('user_id', user!.id).order('name')
      return (data ?? []).map(p => {
        const b = Array.isArray(p.brands) ? p.brands[0] : p.brands
        return { ...p, brandName: (b as { name: string } | null)?.name ?? null }
      }) as ProfileOption[]
    },
  })

  const setProfile = useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase.from('campaigns')
        .update({ sending_profile_id: profileId || null })
        .eq('id', campaignId!).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  })

  const saveDetails = useMutation({
    mutationFn: async (v: { name: string; description: string }) => {
      const { error } = await supabase.from('campaigns')
        .update({ name: v.name.trim(), description: v.description.trim() || null })
        .eq('id', campaignId!).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => { setEditingTitle(null); qc.invalidateQueries({ queryKey: ['campaign', campaignId] }); qc.invalidateQueries({ queryKey: ['campaigns'] }) },
  })

  const setRate = useMutation({
    mutationFn: async (patch: Record<string, number | null>) => {
      const { error } = await supabase.from('campaigns')
        .update(patch).eq('id', campaignId!).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  })

  const setAutoSend = useMutation({
    mutationFn: async (auto_send: boolean) => {
      const { error } = await supabase.from('campaigns')
        .update({ auto_send }).eq('id', campaignId!).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  })

  const setPublic = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { error } = await supabase.from('campaigns').update(patch).eq('id', campaignId!).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  })

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: async () => {
      const { data } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', campaignId!)
        .eq('user_id', user!.id)
        .single()
      return data as CampaignRow | null
    },
    enabled: !!campaignId && !!user,
  })

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>
  if (!campaign) return (
    <div className="text-center py-20 text-gray-500">
      Campaign not found. <Link to="/campaigns" className="text-brand-600 underline">Back to campaigns</Link>
    </div>
  )

  // Sequence campaigns have no Issues tab, so the default differs by kind.
  const tab: Tab = tabOverride ?? (campaign.kind === 'sequence' ? 'sequence' : campaign.kind === 'individual' ? 'outreach' : campaign.kind === 'weekly' ? 'weekly' : campaign.kind === 'tm' ? 'queue' : 'issues')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/campaigns')} className="btn-secondary p-2">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 select-none"
          style={{ backgroundColor: campaign.colour + '20' }}
        >
          {campaign.icon}
        </div>
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <div className="space-y-1.5">
              <input
                autoFocus
                className="input py-1.5 font-semibold"
                value={editingTitle.name}
                onChange={e => setEditingTitle({ ...editingTitle, name: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter' && editingTitle.name.trim()) { e.preventDefault(); saveDetails.mutate(editingTitle) }
                  if (e.key === 'Escape') setEditingTitle(null)
                }}
                placeholder="Campaign name"
              />
              <input
                className="input py-1.5 text-sm"
                value={editingTitle.description}
                onChange={e => setEditingTitle({ ...editingTitle, description: e.target.value })}
                placeholder="Description (optional)"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => editingTitle.name.trim() && saveDetails.mutate(editingTitle)}
                  disabled={!editingTitle.name.trim() || saveDetails.isPending}
                  className="btn-primary text-xs py-1"
                >
                  Save
                </button>
                <button onClick={() => setEditingTitle(null)} className="btn-secondary text-xs py-1">Cancel</button>
              </div>
              {saveDetails.isError && (
                <p className="text-xs text-red-600 font-mono">{describeError(saveDetails.error)}</p>
              )}
            </div>
          ) : (
            <div className="group/title flex items-start gap-2">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900">{campaign.name}</h1>
                {campaign.description && (
                  <p className="text-sm text-gray-500 mt-0.5">{campaign.description}</p>
                )}
              </div>
              <button
                onClick={() => setEditingTitle({ name: campaign.name, description: campaign.description ?? '' })}
                className="mt-1 flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-brand-600 px-2 py-1 rounded-lg border border-gray-200 hover:border-brand-300 transition-colors shrink-0"
                title="Rename campaign"
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sending profile + rate. The profile is locked once set — it carries the
          brand, which decides whose unsubscribes apply. */}
      <div className="card p-3 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Send className="w-3 h-3" /> Sending profile</label>
          {(() => {
            const current = (profileOptions ?? []).find(p => p.id === campaign.sending_profile_id)
            // Not set yet — nothing to protect, let them pick freely.
            if (!campaign.sending_profile_id || profileUnlocked) {
              return (
                <div className="flex items-center gap-1.5">
                  <select
                    className="input py-1.5 text-xs w-56"
                    value={campaign.sending_profile_id ?? ''}
                    onChange={e => {
                      const next = (profileOptions ?? []).find(p => p.id === e.target.value)
                      const changingBrand = !!current && current.brandName !== next?.brandName
                      if (changingBrand && !confirm(
                        `This changes the brand from "${current?.brandName ?? 'none'}" to "${next?.brandName ?? 'none'}".\n\n` +
                        'Unsubscribes are recorded per brand, so people who opted out of the old brand ' +
                        'would start receiving these emails again.\n\nChange it anyway?'
                      )) return
                      setProfile.mutate(e.target.value)
                      setProfileUnlocked(false)
                    }}
                  >
                    <option value="">Not set (pick one)</option>
                    {(profileOptions ?? []).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.from_email}){p.brandName ? ` — ${p.brandName}` : ''}
                      </option>
                    ))}
                  </select>
                  {profileUnlocked && (
                    <button onClick={() => setProfileUnlocked(false)} className="text-[11px] text-gray-400 hover:text-gray-700 px-1">
                      Cancel
                    </button>
                  )}
                </div>
              )
            }
            // Set — show it read-only behind an explicit unlock.
            return (
              <div className="flex items-center gap-2">
                <div className="w-56 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-xs min-w-0">
                  <p className="text-gray-700 truncate">{current?.from_email ?? 'Unknown profile'}</p>
                  {current?.brandName
                    ? <p className="text-[10px] text-indigo-600 truncate">{current.brandName}</p>
                    : <p className="text-[10px] text-amber-600">No brand</p>}
                </div>
                <button
                  onClick={() => {
                    if (confirm(
                      'Change the sending profile for this campaign?\n\n' +
                      'The profile carries the brand, and unsubscribes are recorded per brand. ' +
                      'Switching to a profile on a different brand means people who opted out of ' +
                      'this one could be emailed again.'
                    )) setProfileUnlocked(true)
                  }}
                  className="flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-brand-600 px-2 py-1.5 rounded-lg border border-gray-200 hover:border-brand-300 transition-colors"
                  title="Locked to prevent accidental changes"
                >
                  <Lock className="w-3 h-3" /> Change
                </button>
              </div>
            )
          })()}
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Per hour</label>
          <input type="number" min={1} className="input py-1.5 text-xs w-24" placeholder="inherit"
            defaultValue={campaign.batch_size ?? ''}
            onBlur={e => setRate.mutate({ batch_size: e.target.value ? Math.max(1, Number(e.target.value)) : null })} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Daily cap</label>
          <input type="number" min={1} className="input py-1.5 text-xs w-24" placeholder="inherit"
            defaultValue={campaign.daily_cap ?? ''}
            onBlur={e => setRate.mutate({ daily_cap: e.target.value ? Math.max(1, Number(e.target.value)) : null })} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Throttle (sec)</label>
          <input type="number" min={0} className="input py-1.5 text-xs w-24" placeholder="inherit"
            defaultValue={campaign.throttle_ms != null ? Math.round(campaign.throttle_ms / 1000) : ''}
            onBlur={e => setRate.mutate({ throttle_ms: e.target.value !== '' ? Math.max(0, Number(e.target.value)) * 1000 : null })} />
        </div>
        <p className="text-[10px] text-gray-400 flex-1 min-w-[140px]">Blank = inherit the sending profile. Overrides apply to every issue in this campaign.</p>
      </div>

      {/* Auto-send — never miss a scheduled send */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-700">Auto-send</span>
            <span
              className="inline-flex cursor-help"
              title={
                'If an issue in this campaign is still a draft when its scheduled time arrives, ' +
                'HQ sends it automatically — writing the content with AI first if the draft is empty ' +
                '(The Dossier) — so a send is never missed. You get a review email ~24h before with the ' +
                'draft and an Edit button. Auto-handled issues show as “Scheduled by AI” then “Sent by AI”.'
              }
            >
              <Info className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5 max-w-md">
            Never miss a weekly send. A review email goes out ~24h before; do nothing and it sends on time.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={campaign.auto_send}
          onClick={() => setAutoSend.mutate(!campaign.auto_send)}
          disabled={setAutoSend.isPending}
          className={
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ' +
            (campaign.auto_send ? 'bg-brand-600' : 'bg-gray-300')
          }
          title={campaign.auto_send ? 'Auto-send is on' : 'Auto-send is off'}
        >
          <span
            className={
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform ' +
              (campaign.auto_send ? 'translate-x-6' : 'translate-x-1')
            }
          />
        </button>
      </div>

      {/* Default audience — every new issue starts here; per-issue picker overrides it */}
      {(() => {
        const da = (campaign.default_audience as { campaignLeads?: boolean; allSubscribed?: boolean; segmentIds?: string[] } | null) ?? {}
        const segIds = da.segmentIds ?? []
        const toggleSeg = (id: string) => setDefaultAudience.mutate({
          campaignLeads: !!da.campaignLeads,
          allSubscribed: !!da.allSubscribed,
          segmentIds: segIds.includes(id) ? segIds.filter(x => x !== id) : [...segIds, id],
        })
        const setFlag = (k: 'campaignLeads' | 'allSubscribed', v: boolean) => setDefaultAudience.mutate({
          campaignLeads: k === 'campaignLeads' ? v : !!da.campaignLeads,
          allSubscribed: k === 'allSubscribed' ? v : !!da.allSubscribed,
          segmentIds: segIds,
        })
        const nothing = !da.campaignLeads && !da.allSubscribed && segIds.length === 0
        // Locked once a default is set, so a stray click can't change who a
        // weekly issue targets. Change is behind an explicit confirm.
        const locked = !nothing && !audienceUnlocked
        const chips: string[] = [
          ...(da.campaignLeads ? ["This campaign's leads"] : []),
          ...(da.allSubscribed ? ['All subscribed members'] : []),
          ...(segments ?? []).filter(sg => segIds.includes(sg.id)).map(sg => sg.name),
        ]
        return (
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Default audience</span>
              <span className="text-[11px] text-gray-400">— every new issue starts targeting this; you can still change it per issue</span>
              {locked && (
                <button
                  onClick={() => {
                    if (confirm('Change the default audience for this campaign?\n\nEvery NEW issue will start targeting whatever you set here. Existing issues keep their own audience.')) setAudienceUnlocked(true)
                  }}
                  className="ml-auto flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-brand-600 px-2 py-0.5 rounded border border-gray-200 hover:border-brand-300 transition-colors"
                  title="Locked to prevent accidental changes"
                >
                  <Lock className="w-3 h-3" /> Change
                </button>
              )}
              {!locked && !nothing && (
                <button
                  onClick={() => setAudienceUnlocked(false)}
                  className="ml-auto flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-900 px-2 py-0.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <Lock className="w-3 h-3" /> Done — lock
                </button>
              )}
            </div>

            {locked ? (
              <div className="flex flex-wrap gap-1.5">
                {chips.map(c => (
                  <span key={c} className="text-[11px] px-2.5 py-1 rounded-full border font-medium bg-indigo-50 text-indigo-700 border-indigo-200">{c}</span>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setFlag('campaignLeads', !da.campaignLeads)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${da.campaignLeads ? 'bg-brand-50 text-brand-700 border-brand-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                >
                  {da.campaignLeads ? '✓ ' : ''}This campaign's leads
                </button>
                <button
                  onClick={() => setFlag('allSubscribed', !da.allSubscribed)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${da.allSubscribed ? 'bg-brand-50 text-brand-700 border-brand-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                >
                  {da.allSubscribed ? '✓ ' : ''}All subscribed members
                </button>
                {(segments ?? []).map(sg => (
                  <button
                    key={sg.id}
                    onClick={() => toggleSeg(sg.id)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${segIds.includes(sg.id) ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                  >
                    {segIds.includes(sg.id) ? '✓ ' : ''}{sg.name}
                  </button>
                ))}
              </div>
            )}
            {nothing && (
              <p className="text-[10px] text-gray-400 mt-1.5">No default set — new issues start with nothing selected. Pick a segment above to target it automatically.</p>
            )}
          </div>
        )
      })()}

      {/* Public subscribe page */}
      {campaign.kind !== 'individual' && (
        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" checked={!!campaign.public_subscribe} onChange={e => setPublic.mutate({ public_subscribe: e.target.checked })} />
            List on the public subscribe page
          </label>
          {campaign.public_subscribe && (
            <div className="space-y-2 pl-6">
              <input defaultValue={campaign.public_title ?? ''} placeholder="Public title (e.g. The Weekly Dossier)"
                onBlur={e => e.target.value !== (campaign.public_title ?? '') && setPublic.mutate({ public_title: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm" />
              <textarea defaultValue={campaign.public_description ?? ''} rows={2} placeholder="Short explanation of what it is and what subscribers get…"
                onBlur={e => e.target.value !== (campaign.public_description ?? '') && setPublic.mutate({ public_description: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm" />
              <p className="text-[11px] text-gray-500">
                Live at <a href="/subscribe" target="_blank" rel="noreferrer" className="text-brand-600 underline">/subscribe</a>.
                Subscribers land in a segment called “{(campaign.public_title || campaign.name)} subscribers” — target it in this campaign’s issues to reach them.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          ...(campaign.kind === 'sequence'
            ? [['sequence', <Workflow className="w-3.5 h-3.5" />, 'Sequence'] as const]
            : campaign.kind === 'individual'
            ? [['outreach', <Workflow className="w-3.5 h-3.5" />, 'Outreach'] as const]
            : campaign.kind === 'weekly'
            ? [['weekly', <CalendarClock className="w-3.5 h-3.5" />, 'Weekly Email'] as const]
            : campaign.kind === 'tm'
            ? [['queue', <Phone className="w-3.5 h-3.5" />, 'Call Queue'] as const]
            : [['issues', <Mail className="w-3.5 h-3.5" />, 'Issues'] as const]),
          ...(campaign.kind === 'individual' || campaign.kind === 'weekly' || campaign.kind === 'tm' ? [] : [['templates', <FileText className="w-3.5 h-3.5" />, 'Templates'] as const]),
          ...(campaign.kind === 'tm' ? [] : [['members', <Users className="w-3.5 h-3.5" />, 'Lead Members'] as const]),
        ] as const).map(([t, icon, label]) => (
          <button
            key={t}
            onClick={() => setTabOverride(t)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'sequence' && (
        <SequenceContent campaignId={campaign.id} />
      )}
      {tab === 'outreach' && (
        <IndividualContent campaignId={campaign.id} />
      )}
      {tab === 'weekly' && (
        <WeeklyContent campaignId={campaign.id} />
      )}
      {tab === 'queue' && (
        <TMPage campaignId={campaign.id} embedded />
      )}
      {tab === 'issues' && (
        <IssuesContent campaignId={campaign.id} autoSend={campaign.auto_send} />
      )}
      {tab === 'templates' && (
        <TemplatesContent campaignId={campaign.id} />
      )}
      {tab === 'members' && (
        <MembersContent campaignId={campaign.id} />
      )}
    </div>
  )
}
